import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MicrositeContent,
  MicrositeScreen,
  MicrositeClubAverage,
} from '@/types/microsite';
import { micrositeService } from '@/services/microsite.service';
import './microsite-report.css';

/*
 * Assessment report (rev 4 template).
 * Data-driven from MicrositeContent. Sections marked `TEMPLATE` render placeholder
 * content until their data source lands (see ENG-557..562):
 *   - Athlete Profile / archetype  → ENG-558 (archetype capture) + GOAL_MODEL_SPEC §8a
 *   - tour-range band + smash targets → ENG-557 A4 goal model (optimalRange)
 *   - "Work on this" reco chips → ENG-559 content-library join
 *   - Box Pro identity → coachUserId resolution
 * Everything else (name, TPI screens, SportsBox scores, live TrackMan, narrative)
 * is bound to real data.
 */

const CLUB_LABEL: Record<string, string> = {
  driver: 'Driver', '7iron': '7-Iron', Wood: 'Fairway Wood', Hybrid: 'Hybrid', Wedge: 'Wedge',
};
// TEMPLATE: archetype-adjusted smash windows — source from the goal model per club.
const SMASH_TARGETS: Record<string, string> = {
  driver: '1.45–1.50', '7iron': '1.30–1.38', Wood: '1.42–1.48', Hybrid: '1.40–1.46', Wedge: '1.18–1.25',
};
const TOUR_MIN = 70; // TEMPLATE: SportsBox tour-range reference (goal model band)
const MIN_SHOTS_FOR_SPREAD = 5;

// ENG-559: the three plan lanes, with a short contextual note each. Body/Swing
// are matched to the player's assessment; Game is the universal practice library.
const PLAN_LANES = [
  { key: 'body', label: 'Body', note: 'Mobility, stability & strength matched to your screens.' },
  { key: 'swing', label: 'Swing', note: 'Targeting the faults your swing showed.' },
  { key: 'game', label: 'Game', note: 'Your on-course practice library.' },
] as const;

const clubLabel = (c: string) => CLUB_LABEL[c] ?? c;

function NavBar({ ids }: { ids: [string, string][] }) {
  return (
    <nav>
      <div className="logo">TEE<br />BOX</div>
      <div className="links">
        {ids.map(([id, label]) => (
          <a key={id} href={`#${id}`} data-s={id}>{label}</a>
        ))}
      </div>
    </nav>
  );
}

function Meter({ label, value }: { label: string; value?: number }) {
  if (value == null) return null;
  const good = value >= TOUR_MIN;
  return (
    <div className="metric">
      <div className="top">
        <span className="mn">{label}</span>
        <span className={`mv ${good ? 'good' : 'bad'}`}>{value} {good ? '▲' : '▼'}</span>
      </div>
      <div className="bar">
        <div className="band" style={{ left: `${TOUR_MIN}%`, right: 0 }} />
        <div className="fill" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: good ? 'var(--green-fill)' : 'var(--maroon)' }} />
        <div className="tgt" style={{ left: `${TOUR_MIN}%` }} />
      </div>
      <div className="scalerow"><span>0</span><span>tour range {TOUR_MIN}–100</span><span>100</span></div>
      {good
        ? <span className="ok">▲ above tour range</span>
        : <span className="flag">▼ {TOUR_MIN - value} below the tour range — the leak in your chain</span>}
    </div>
  );
}

export default function MicrositeReportPage({ sampleData }: { sampleData?: MicrositeContent }) {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<MicrositeContent | null>(sampleData ?? null);
  const [loading, setLoading] = useState(!sampleData);

  useEffect(() => {
    if (sampleData) { setData(sampleData); setLoading(false); return; }
    if (!shareId) { navigate('/p/error', { replace: true }); return; }
    micrositeService.getContent(shareId)
      .then(setData)
      .catch(() => navigate('/p/error', { replace: true }))
      .finally(() => setLoading(false));
  }, [shareId, navigate, sampleData]);

  // scroll-spy: highlight the nav link for the section in view
  useEffect(() => {
    if (!data) return;
    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('.tbx-report nav a'));
    const byId: Record<string, HTMLAnchorElement> = {};
    links.forEach((a) => { byId[a.dataset.s ?? ''] = a; });
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          links.forEach((l) => l.classList.remove('active'));
          byId[e.target.id]?.classList.add('active');
        }
      });
    }, { rootMargin: '-45% 0px -50% 0px' });
    ['top', 'profile', 'body', 'swing', 'game', 'plan', 'roadmap', 'next'].forEach((id) => {
      const el = document.getElementById(id); if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#B58F2D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { player, tpi, sportsbox, trackman, narrative, coach } = data;
  const m = sportsbox.metrics ?? {};
  const screens: MicrositeScreen[] = tpi.screens ?? [];
  const passes = screens.filter((s) => s.result === 'pass');
  const room = screens.filter((s) => s.result !== 'pass');

  const clubs: MicrositeClubAverage[] = (trackman?.clubs ?? [])
    .slice()
    .sort((a, b) => b.shots - a.shots);
  const driver = clubs.find((c) => c.club === 'driver');
  const powerClub = driver ?? clubs[0];
  const worstSpread = clubs
    .filter((c) => c.shots >= MIN_SHOTS_FOR_SPREAD && c.carrySpreadYds != null)
    .sort((a, b) => (b.carrySpreadYds ?? 0) - (a.carrySpreadYds ?? 0))[0];

  const pillFor = (r: string) => (r === 'fail' ? ['fail', 'Fail'] : r === 'warn' ? ['warn', 'Limited'] : ['pass', 'Pass']);
  const spreadCell = (c: MicrositeClubAverage) =>
    c.shots < MIN_SHOTS_FOR_SPREAD || c.carrySpreadYds == null;

  // score tiles derived from real data (+ honest pending)
  const tiles = [
    tpi.fitnessHandicap != null && {
      cls: (screens.length && room.length > screens.length / 2) ? 'work' : 'good',
      tl: 'Body · TPI', tv: String(tpi.fitnessHandicap), tvUnit: '',
      ts: screens.length ? `fitness handicap · ${room.length} of ${screens.length} to work on` : 'fitness handicap',
      stat: room.length > screens.length / 2 ? 'Work on it' : 'On track',
    },
    m.overallScore != null && {
      cls: m.overallScore >= TOUR_MIN ? 'good' : 'work',
      tl: 'Swing · SportsBox', tv: String(m.overallScore), tvUnit: '',
      ts: `overall · tour range ${TOUR_MIN}+`, stat: m.overallScore >= TOUR_MIN ? 'Strong' : 'Work on it',
    },
    powerClub?.clubSpeedMph != null && {
      cls: 'good', tl: `Power · ${clubLabel(powerClub.club)}`, tv: String(powerClub.clubSpeedMph), tvUnit: ' mph',
      ts: 'measured club speed', stat: 'Measured',
    },
    { cls: 'pending', tl: 'Game · Combine', tv: '—', tvUnit: '', ts: 'not yet measured', stat: 'Pending' },
    { cls: 'pending', tl: 'Index · Handicap', tv: player.handicapRange ? player.handicapRange.replace(/\s*\(.*\)/, '') : '—', tvUnit: '', ts: 'self-reported baseline', stat: 'Baseline' },
  ].filter(Boolean) as Array<{ cls: string; tl: string; tv: string; tvUnit: string; ts: string; stat: string }>;

  const navIds: [string, string][] = [
    ['top', 'Overview'], ['profile', 'Athlete Profile'], ['body', 'Body'],
    ['swing', 'Swing'], ['game', 'Game'], ['plan', 'Plan'],
    ['roadmap', '12-Month Plan'], ['next', 'Next Steps'],
  ];

  // Roadmap cadence — a Combine every 30 days with a ~15-min review, and a full
  // golf performance assessment every 90 days (the hero milestones). Months are
  // projected forward from the assessment's completedAt (falling back to today
  // when it's missing); the footer notes they shift with real scheduling.
  const roadmapAnchor = data.completedAt ? new Date(data.completedAt) : new Date();
  const roadmapAnchorValid = !Number.isNaN(roadmapAnchor.getTime());
  const roadmapMonths = Array.from({ length: 12 }, (_, i) => {
    const n = i + 1;
    const d = roadmapAnchorValid ? new Date(roadmapAnchor) : new Date();
    d.setMonth(d.getMonth() + n);
    return {
      n,
      mo: d.toLocaleDateString(undefined, { month: 'short' }),
      q: Math.ceil(n / 3),
      hero: n % 3 === 0,
    };
  });
  const roadmapQuarters = [1, 2, 3, 4].map((q) => ({
    q,
    months: roadmapMonths.filter((mm) => mm.q === q),
  }));

  const proName = 'Your Box Pro'; // TEMPLATE: resolve from coachUserId

  return (
    <div className="tbx-report">
      <NavBar ids={navIds} />

      {/* HERO */}
      <section id="top" className="forest hero">
        <div className="inner">
          <div className="eyebrow on">Golf Performance Assessment · Baseline</div>
          <h1>{player.name}</h1>
          <p className="sub">Your baseline, top to bottom — how you're built, what we measured, and what it means for the shots that count.</p>
          <div className="propill">
            {coach?.photoUrl && <img src={coach.photoUrl} alt="" />}
            Your Box Pro <b>{coach?.name ?? proName}</b>
            {coach?.phone && <a href={`sms:${coach.phone}`}>Message</a>}
            {coach?.phone && <a href={`tel:${coach.phone}`}>Call</a>}
            {coach?.email && <a href={`mailto:${coach.email}`}>Email</a>}
          </div>

          <div className="tiles">
            {tiles.map((t, i) => (
              <div key={i} className={`tile ${t.cls}`}>
                <div className="tl">{t.tl}</div>
                <div className="tv">{t.tv}{t.tvUnit && <small>{t.tvUnit}</small>}</div>
                <div className="ts">{t.ts}</div>
                <span className="tstat">{t.stat}</span>
              </div>
            ))}
          </div>

          {/* TEMPLATE: archetype matchup — bind to captured Fold×Post */}
          <p className="matchline">Your matchup: <b>On Top Fold × Center Post</b> — a fade-shaped, drop-and-rise mover. We coach to your design, not a textbook ideal. <a href="#profile">See your athlete profile →</a></p>

          <p className="chainlead">And your game is a chain — each step sets up the next, read left to right:</p>
          <div className="chainwrap">
            <div className="chain">
              <div className="step"><div className="n">1</div><div className="st">Your Body</div><div className="sd">TPI screen — what you can physically do.</div></div>
              <div className="harrow"><span className="g">→</span><span className="l">enables</span></div>
              <div className="step"><div className="n">2</div><div className="st">Your Swing</div><div className="sd">SportsBox 3D — how your body moves.</div></div>
              <div className="harrow"><span className="g">→</span><span className="l">produces</span></div>
              <div className="step"><div className="n">3</div><div className="st">Club Data</div><div className="sd">TrackMan impact — path, face, smash.</div></div>
              <div className="harrow"><span className="g">→</span><span className="l">causes</span></div>
              <div className="step"><div className="n">4</div><div className="st">Ball Flight</div><div className="sd">The outcome — carry, spin, dispersion.</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 01 · ATHLETE PROFILE — TEMPLATE (archetype capture pending) */}
      <section id="profile" className="athlete">
        <div className="inner">
          <div className="eyebrow">Section 01</div>
          <h2 className="sec-title">Your Athlete Profile</h2>
          <div className="rule" />
          <p className="lede">Two things about how you're built rarely change — and they should shape every swing you make. At Tee Box we coach to <i>your</i> design.</p>
          <div className="matchcard">
            <div>
              <h3>Your matchup — On Top Fold × Center Post</h3>
              <div className="prose">
                <p>Every golfer at Tee Box is one of nine player types — an upper-body <b>fold</b> crossed with a lower-body <b>post</b>. You are <b>On Top Fold · Center Post</b>: your trail arm folds high and on top, and you pivot around both hips with a drop-and-rise.</p>
                <p><b>What it means:</b> a mild out-to-in path and a slight squat before impact are <i>correct</i> for you — your engine, not a flaw. Your job is to <b>sharpen the fade and time the drop</b>, not erase them.</p>
              </div>
            </div>
            <div className="powerpanel">
              <div className="pk">Power source</div>
              <div className="pv">Drop &amp; rise</div>
              <div className="pd">Center-post squat — stored energy releases upward.</div>
              <div className="pk">Tour pattern-mates</div>
              <div className="pd">On Top fold: McIlroy, Scheffler. Center post: Tiger Woods.</div>
              <div className="pk">Coaching rule</div>
              <div className="pd">Keep the fade and the squat — tighten <i>when</i> and <i>how much</i>.</div>
            </div>
          </div>
          <div className="whycallout"><b style={{ fontFamily: 'var(--sans)' }}>Why two axes and not a number:</b> every golfer at Tee Box is a fold and a post — nine combinations, but you never memorise a list. Your post is your engine; your fold is how the club gets delivered.</div>
        </div>
      </section>

      {/* BODY */}
      <section id="body" className="alt">
        <div className="inner">
          <div className="sechead"><div className="eyebrow">Step 1 of the chain</div><h2 className="sec-title">Your Body</h2><span className="sectag">TPI movement screen</span></div>
          <div className="rule" />
          <p className="lede">Your body sets the ceiling for everything above it. Here's what's working, and where the room is.</p>
          {screens.length === 0
            ? <p className="means">TPI screen not available yet.</p>
            : (
              <>
                <div className="grid2" style={{ marginTop: 22 }}>
                  <div className="card">
                    <h4>What's working</h4>
                    {passes.map((s) => (
                      <div className="scrn" key={s.id}><div className="nm">{s.label}</div><span className="pill pass">Pass</span></div>
                    ))}
                    {passes.length === 0 && <p className="means">Building here — no clear passes yet.</p>}
                  </div>
                  <div className="card">
                    <h4>Where the room is</h4>
                    {room.map((s) => {
                      const [cls, txt] = pillFor(s.result);
                      return <div className="scrn" key={s.id}><div className="nm">{s.label}</div><span className={`pill ${cls}`}>{txt}</span></div>;
                    })}
                  </div>
                </div>
                <div className="means"><b>What this means:</b> a <i>limited</i> screen isn't failing — but the freedom or control isn't there under load, so the swing borrows the motion from elsewhere. {tpi.fitnessHandicap != null && <><b>Fitness handicap {tpi.fitnessHandicap}.</b></>}</div>
              </>
            )}
        </div>
      </section>

      {/* SWING */}
      <section id="swing">
        <div className="inner">
          <div className="sechead"><div className="eyebrow">Step 2 of the chain</div><h2 className="sec-title">Your Swing</h2><span className="sectag">SportsBox 3D · read vs your archetype</span></div>
          <div className="rule" />
          <p className="lede">How your body moves, in 3D. {m.overallScore != null && <>Your overall swing is <b style={{ color: 'var(--ink)' }}>{m.overallScore}</b> — each score below is plotted against the tour range.</>}</p>
          <div className="card" style={{ marginTop: 22 }}>
            <Meter label="Speed" value={m.speedScore} />
            <Meter label="Efficiency" value={m.efficiencyScore} />
            <Meter label="Consistency" value={m.consistencyScore} />
            {sportsbox.summary && <p className="means">{sportsbox.summary}</p>}
          </div>
        </div>
      </section>

      {/* GAME */}
      <section id="game" className="alt">
        <div className="inner">
          <div className="sechead"><div className="eyebrow">Steps 3 &amp; 4 of the chain</div><h2 className="sec-title">Your Game</h2><span className="sectag">TrackMan{trackman?.totalShots ? ` · ${trackman.totalShots} shots` : ''}</span></div>
          <div className="rule" />
          <p className="lede">What the club and ball actually did. Where we have a target for your club you'll see it; where we don't yet, we say so.</p>
          {clubs.length === 0
            ? <p className="means" style={{ marginTop: 22 }}>No live TrackMan data yet.</p>
            : (
              <div className="tmwrap" style={{ marginTop: 22 }}>
                <table className="tm">
                  <thead><tr><th>Club</th><th>Shots</th><th>Club mph</th><th>Carry</th><th>Launch</th><th>Spin</th><th>Smash</th><th>Smash target</th><th>Carry spread</th></tr></thead>
                  <tbody>
                    {clubs.map((c) => {
                      const suppress = spreadCell(c);
                      const flagged = !suppress && c === worstSpread;
                      return (
                        <tr key={c.club} className={c.shots < MIN_SHOTS_FOR_SPREAD ? 'thin' : undefined}>
                          <td>{clubLabel(c.club)}</td>
                          <td>{c.shots}</td>
                          <td>{c.clubSpeedMph ?? '—'}</td>
                          <td>{c.carryYds ?? '—'}</td>
                          <td>{c.launchAngleDeg != null ? `${c.launchAngleDeg}°` : '—'}</td>
                          <td>{c.spinRpm != null ? c.spinRpm.toLocaleString() : '—'}</td>
                          <td>{c.smash ?? '—'}</td>
                          <td>{SMASH_TARGETS[c.club] ?? '—'}</td>
                          <td className={flagged ? 'flagcell' : undefined}>
                            {suppress ? <>—<span className="tfn">too few shots</span></> : `${c.carrySpreadYds} yd${flagged ? ' · wide' : ''}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="tmcards">
                  {clubs.map((c) => {
                    const suppress = spreadCell(c);
                    const flagged = !suppress && c === worstSpread;
                    return (
                      <div className="tmcard" key={c.club} style={c.shots < MIN_SHOTS_FOR_SPREAD ? { opacity: 0.6 } : undefined}>
                        <div className="h"><span>{clubLabel(c.club)} · {c.shots} shots</span><span>{c.carryYds ?? '—'} yd</span></div>
                        <div className="g">
                          <span className="k">Club speed</span><span>{c.clubSpeedMph ?? '—'} mph</span>
                          <span className="k">Smash</span><span>{c.smash ?? '—'} {SMASH_TARGETS[c.club] && <small>(tgt {SMASH_TARGETS[c.club]})</small>}</span>
                          <span className="k">Carry spread</span><span style={flagged ? { color: 'var(--maroon)', fontWeight: 700 } : undefined}>{suppress ? '— too few shots' : `${c.carrySpreadYds} yd${flagged ? ' · wide' : ''}`}</span>
                          <span className="k">Launch / Spin</span><span>{c.launchAngleDeg ?? '—'}° / {c.spinRpm != null ? c.spinRpm.toLocaleString() : '—'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          <p className="legend"><span className="dot" style={{ background: 'var(--maroon)' }} />flagged &nbsp; Smash targets are archetype-adjusted. AoA, club path and face aren't captured yet.</p>
          {narrative && (
            <div className="callout"><div className="h">The leak — and it matches your swing</div>{narrative.chain.ball.detail || narrative.prose}</div>
          )}
        </div>
      </section>

      {/* YOUR PLAN — the live generated plan (ENG-559), grouped by lane */}
      <section id="plan">
        <div className="inner">
          <div className="sechead"><div className="eyebrow">Curated for you</div><h2 className="sec-title">Your Plan</h2><span className="sectag">Matched to your profile</span></div>
          <div className="rule" />
          <p className="lede">Your plan, built from this assessment. Body &amp; Swing drills target what your assessment flagged; Game is your practice library.</p>
          <div className="plan3">
            {PLAN_LANES.map(({ key, label, note }) => {
              const items = data.plan?.[key] ?? [];
              return (
                <div className="pblock" key={key}>
                  <div className="ph"><span className="pn">{items.length}<small> drill{items.length === 1 ? '' : 's'}</small></span><span className="pt">{label}</span></div>
                  <div className="pd">{note}</div>
                  {items.length > 0 ? (
                    <ul className="plist">{items.map((it) => <li key={it.id}>{it.title}</li>)}</ul>
                  ) : (
                    <div className="pd">No {label.toLowerCase()} drills yet — they appear as your assessment data grows.</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* YOUR NEXT 12 MONTHS — cadence roadmap: monthly combines + 90-day assessments */}
      <section id="roadmap" className="alt">
        <div className="inner">
          <div className="sechead"><div className="eyebrow">Your next 12 months</div><h2 className="sec-title">The rhythm ahead</h2></div>
          <div className="rule" />
          <p className="lede">Beyond your plan, here's the cadence we'll keep together — a Combine every 30 days with a quick review to measure progress and adjust what's next, and a full golf performance assessment every 90 days.</p>
          <div className="rm-legend">
            <span><i className="rm-sw combine" />Combine <small>· every 30 days</small></span>
            <span><i className="rm-sw review" />Box Pro review <small>· ~15 min, monthly</small></span>
            <span><i className="rm-sw reassess" />Golf performance assessment <small>· every 90 days</small></span>
          </div>
          <div className="rm-tl">
            {roadmapQuarters.map(({ q, months }) => {
              const routine = months.filter((mm) => !mm.hero);
              const hero = months.find((mm) => mm.hero);
              return (
                <div key={q} className="rm-qgroup">
                  <div className="rm-qlabel">Quarter {q}</div>
                  {routine.map((mm) => (
                    <div key={mm.n} className="rm-row">
                      <div className="rm-mk"><span className="rm-n">M{mm.n}</span><span className="rm-mo">{mm.mo}</span></div>
                      <div className="rm-rail"><span className="rm-node" /></div>
                      <div className="rm-acts">
                        <span className="rm-act"><i className="rm-sw combine" />Combine</span>
                        <span className="rm-act"><i className="rm-sw review" />Box Pro review · ~15 min</span>
                      </div>
                    </div>
                  ))}
                  {hero && (
                    <div className="rm-hero">
                      <span className="rm-hero-accent" />
                      <span className="rm-medal"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0 0 12 0V2Z" /></svg></span>
                      <div className="rm-hero-body">
                        <div className="rm-hero-eyebrow">{q === 4 ? 'One-Year Milestone' : '90-Day Milestone'} · Quarter {q}</div>
                        <h3>Full Golf Performance Assessment</h3>
                        <p>A complete re-read of your game — TPI screen, SportsBox 3D swing and TrackMan launch. Replaces this month's routine review.</p>
                        <span className="rm-chip"><i className="rm-sw reassess" />Includes your Month {hero.n} Combine</span>
                      </div>
                      <div className="rm-hero-meta"><span className="rm-when">{hero.mo}</span><span className="rm-whenlbl">Month {hero.n}</span></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="rm-note"><b>One visit, not two.</b> Your Combine and the review with your Box Pro are scheduled back-to-back in a single visit — measure, then talk it through while the numbers are fresh.</div>
          <p className="legend">Dates are projected from your assessment and shift with your actual visit scheduling.</p>
        </div>
      </section>

      {/* COACH NOTE (pro-owned) + NEXT STEPS */}
      <section id="next" className="forest">
        <div className="inner">
          {narrative?.oneFocus && (
            <div className="coachnote" style={{ color: 'var(--ink)' }}>
              {coach?.photoUrl && <img src={coach.photoUrl} alt="" />}
              <div>
                <div className="k">From your Box Pro</div>
                <p className="body">{narrative.oneFocus.body || narrative.prose}</p>
                <div className="sig">— {coach?.name ?? proName}{coach?.title ? `, ${coach.title}` : ''}{narrative.generatedBy === 'ai' ? ' · draft, edit before sending' : ''}</div>
              </div>
            </div>
          )}
          <div className="sechead" style={{ marginTop: 40 }}><div className="eyebrow on">Where to from here</div><h2 className="sec-title" style={{ color: 'var(--on-forest)' }}>Your Next Step</h2></div>
          <div className="rule" />
          <div className="primary-cta">
            <div><h3>Become a Performance+ Member</h3><p>The bays, your Box Pro's coaching, and your weekly 15 / 15 / 30 loop — the habit that turns this baseline into strokes.</p></div>
            <a className="btn" href="#">Start free trial →</a>
          </div>
          <div className="seclinks"><a href="#">Book your next Golf Performance Assessment</a><a href="#">Open your content library</a></div>
          <div className="trackline">
            <div><span className="tk">Where you are today</span><span className="tv">{player.handicapRange ? player.handicapRange.replace(/\s*\(.*\)/, '') : '—'}</span><span className="td">self-reported — your re-tests track it moving</span></div>
            <div><span className="tk">Measurement cadence</span><span className="tv">Monthly · Quarterly</span><span className="td">monthly combines; quarterly re-assessments</span></div>
          </div>
          <div className="procard">
            {coach?.photoUrl && <img src={coach.photoUrl} alt="" />}
            <div><div className="nm">{coach?.name ?? 'Your Box Pro'}</div><div className="tt">{coach?.title ?? 'Your Box Pro'}</div></div>
            <div className="acts">
              {coach?.phone && <a className="act" href={`sms:${coach.phone}`}><svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h16v12H7l-3 3V4z" /></svg>Message</a>}
              {coach?.phone && <a className="act" href={`tel:${coach.phone}`}><svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8a15 15 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.24 11 11 0 0 0 3.5.56 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.3a1 1 0 0 1 1 1 11 11 0 0 0 .56 3.5 1 1 0 0 1-.24 1L6.6 10.8z" /></svg>Call</a>}
              {coach?.email && <a className="act" href={`mailto:${coach.email}`}><svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 5h18v14H3V5zm2 2v.5l7 4.5 7-4.5V7H5zm14 3.2-7 4.5-7-4.5V17h14v-6.8z" /></svg>Email</a>}
              <a className="act" href="#" onClick={(e) => { e.preventDefault(); window.print(); }}><svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 9V2h12v7h3v9h-4v4H7v-4H3V9h3zm2-5v5h8V4H8zm8 12H8v4h8v-4zM5 11v5h2v-2h10v2h2v-5H5z" /></svg>Save as PDF</a>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div><span className="fl">TBX.GOLF</span> &nbsp; #KNOWYOURGAME &nbsp; #BUILDTHEHABIT</div>
        <div>Baseline · your plan evolves with every re-assessment.</div>
      </footer>
    </div>
  );
}
